import { useState, type ReactNode } from 'react'

import { pluralize } from '@/lib/pluralize'
import { Button } from '@/ui/button'
import { buttonVariants } from '@/ui/button-variants'
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui/tooltip'

import { useEntityAncestors } from '../lib/entity-queries'
import {
  settingMutationErrorMessage,
  useCanEditSettings,
  useCanReadAudit,
  useResetSetting,
  type SettingResolution,
} from '../lib/setting-queries'
import { settingTargetOf, type SettingTargetProps } from '../lib/setting-target'
import { SettingHistoryDialog } from './setting-history-dialog'

export type OptimisticSetting = 'saved' | 'removed' | null

export type SettingInheritanceStatusProps = SettingTargetProps & {
  resolution: SettingResolution
  optimistic?: OptimisticSetting
  confirmTitle?: string
  confirmDescription?: string
  settingName?: string
  settingLabel?: string
  historyDescription?: ReactNode
  formatValue?: (value: unknown) => string
  unresolvedLabel?: string
  onReset?: () => void
}

export function SettingInheritanceStatus(props: SettingInheritanceStatusProps) {
  const {
    resolution,
    optimistic = null,
    confirmTitle = 'Remove override?',
    confirmDescription = 'This entity falls back to the inherited or platform-default value.',
    settingName,
    settingLabel,
    historyDescription,
    formatValue,
    unresolvedLabel = 'Settings for this entity haven’t been resolved yet.',
    onReset,
  } = props
  const target = settingTargetOf(props)
  const canEdit = useCanEditSettings(target.entityId)
  const canReadAudit = useCanReadAudit(target.entityId)
  const reset = useResetSetting()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isLocal =
    optimistic === 'saved' ||
    (optimistic === null && resolution.provenance === 'local')
  const isInherited = optimistic === null && resolution.provenance === 'inherited'

  const ancestorsQuery = useEntityAncestors(isInherited ? target.entityId : null)
  const ancestorName = resolution.sourceEntityId
    ? ancestorsQuery.data?.items.find((a) => a.entityId === resolution.sourceEntityId)
        ?.name
    : undefined

  async function handleReset() {
    if (!canEdit || reset.isPending) return
    setConfirmOpen(false)
    try {
      setError(null)
      await reset.mutateAsync({ target })
      onReset?.()
    } catch (e) {
      setError(settingMutationErrorMessage(e, 'Couldn’t remove the override. Try again.'))
    }
  }

  const label = statusLabel(optimistic, resolution, ancestorName, unresolvedLabel)

  return (
    <div className="flex flex-col items-start gap-1.5">
      {isInherited ? (
        <TooltipProvider delay={0}>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  className="text-muted-foreground focus-visible:ring-ring/40 cursor-help rounded-sm text-left text-xs font-medium underline decoration-dotted underline-offset-2 focus-visible:ring-2 focus-visible:outline-none"
                />
              }
            >
              {label}
            </TooltipTrigger>
            <TooltipContent>
              Inherited from{' '}
              {ancestorName ?? 'an entity above the ones you can see'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <p className="text-muted-foreground text-xs font-medium">{label}</p>
      )}

      {isLocal && (
        <Popover open={confirmOpen} onOpenChange={setConfirmOpen}>
          <PopoverTrigger
            className={buttonVariants({ variant: 'outline' })}
            disabled={reset.isPending || !canEdit}
            aria-label={namedAction('Remove override', settingName)}
          >
            Remove override
          </PopoverTrigger>
          <PopoverContent align="start">
            <PopoverHeader>
              <PopoverTitle>{confirmTitle}</PopoverTitle>
              <PopoverDescription>{confirmDescription}</PopoverDescription>
            </PopoverHeader>
            <div className="flex justify-end gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => void handleReset()}
              >
                Remove
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}

      {canReadAudit && target.kind === 'resource-default' && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={namedAction('View history', settingName)}
            onClick={() => setHistoryOpen(true)}
          >
            View history
          </Button>
          <SettingHistoryDialog
            target={target}
            settingLabel={settingLabel ?? settingName}
            description={historyDescription}
            formatValue={formatValue}
            open={historyOpen}
            onOpenChange={setHistoryOpen}
          />
        </>
      )}
    </div>
  )
}

function namedAction(action: string, settingName: string | undefined) {
  return settingName === undefined ? undefined : `${action}: ${settingName}`
}

function inheritedLabel(distance: number | null, ancestorName: string | undefined): string {
  const source = `Inherited from ${ancestorName ?? 'an ancestor'}`
  return distance ? `${source} (${pluralize(distance, 'level')} up)` : source
}

function statusLabel(
  optimistic: OptimisticSetting,
  resolution: SettingResolution,
  ancestorName: string | undefined,
  unresolvedLabel: string,
): string {
  if (optimistic === 'removed')
    return 'Override removed. The resolved value updates in the background.'
  if (optimistic === 'saved')
    return 'Saved. The resolved value updates in the background.'
  switch (resolution.provenance) {
    case 'local':
      return 'Local override'
    case 'inherited':
      return inheritedLabel(resolution.inheritedDistance, ancestorName)
    case 'policy':
      return 'Policy value'
    case 'default':
      return 'Platform default'
    case 'unresolved':
      return unresolvedLabel
  }
}
